import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, GraduationCap, Plus, Sparkles, Trash2 } from 'lucide-react';

import {
  getComprehensionTest,
  readOptions,
  saveComprehensionTest,
  suggestComprehensionQuestions,
  type ComprehensionOption,
} from '@/api/sops.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';

/**
 * Editor-only authoring for an SOP's comprehension test (ADR-040).
 *
 * The bar this sets is real: while the test is ACTIVE, passing it is the ONLY
 * way to satisfy the re-acknowledgment obligation — the plain acknowledge
 * button refuses. So the UI is deliberately explicit about activation, and
 * refuses to activate an empty test.
 *
 * AI drafts questions from the SOP text; a human edits and activates. Drafted
 * questions are flagged until touched, so review effort goes where it is
 * needed.
 */

interface DraftQuestion {
  /** Local key — server ids are not round-tripped; questions save wholesale. */
  key: string;
  prompt: string;
  options: ComprehensionOption[];
  correctOptionId: string;
  explanation: string | null;
  aiDrafted: boolean;
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `q${String(keySeq)}`;
}

function blankQuestion(): DraftQuestion {
  return {
    key: nextKey(),
    prompt: '',
    options: [
      { id: 'o1', text: '' },
      { id: 'o2', text: '' },
    ],
    correctOptionId: 'o1',
    explanation: null,
    aiDrafted: false,
  };
}

export function ComprehensionTestCard({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const { show } = useToast();

  const testQuery = useQuery({
    queryKey: ['sop-comprehension', slug],
    queryFn: () => getComprehensionTest(slug),
    enabled: Boolean(slug),
  });

  const [questions, setQuestions] = useState<DraftQuestion[]>([]);
  const [threshold, setThreshold] = useState(80);
  const [maxAttempts, setMaxAttempts] = useState<string>('');
  const [active, setActive] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Hydrate the editor once from the server, then leave local edits alone.
  useEffect(() => {
    if (loaded || testQuery.data === undefined) return;
    const test = testQuery.data;
    if (test) {
      setThreshold(test.passThresholdPercent);
      setMaxAttempts(test.maxAttempts === null ? '' : String(test.maxAttempts));
      setActive(test.active);
      setQuestions(
        test.questions.map((q) => ({
          key: nextKey(),
          prompt: q.prompt,
          options: readOptions(q.optionsJson),
          correctOptionId: q.correctOptionId ?? 'o1',
          explanation: q.explanation ?? null,
          aiDrafted: q.aiDrafted,
        })),
      );
    }
    setLoaded(true);
  }, [testQuery.data, loaded]);

  const suggestMutation = useMutation({
    mutationFn: () => suggestComprehensionQuestions(slug),
    onSuccess: (res) => {
      setQuestions((prev) => [
        ...prev,
        ...res.questions.map((q) => ({
          key: nextKey(),
          prompt: q.prompt,
          options: q.options,
          correctOptionId: q.correctOptionId,
          explanation: q.explanation,
          aiDrafted: true,
        })),
      ]);
      show(
        res.droppedCount > 0
          ? `Drafted ${String(res.questions.length)} question(s); discarded ${String(res.droppedCount)} malformed.`
          : `Drafted ${String(res.questions.length)} question(s) — review before activating.`,
        { variant: 'success' },
      );
    },
    onError: () => {
      show('Could not draft questions. Check that the AI key is configured.', { variant: 'danger' });
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      saveComprehensionTest(slug, {
        passThresholdPercent: threshold,
        maxAttempts: maxAttempts.trim() === '' ? null : Number(maxAttempts),
        active,
        questions: questions.map((q) => ({
          prompt: q.prompt.trim(),
          options: q.options.map((o) => ({ id: o.id, text: o.text.trim() })),
          correctOptionId: q.correctOptionId,
          explanation: q.explanation,
          aiDrafted: q.aiDrafted,
        })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sop-comprehension', slug] });
      show('Comprehension test saved.', { variant: 'success' });
    },
    onError: () => {
      show('Could not save the test.', { variant: 'danger' });
    },
  });

  function patch(key: string, next: Partial<DraftQuestion>): void {
    setQuestions((prev) =>
      // Any human edit clears the ai-drafted flag — it means "unreviewed".
      prev.map((q) => (q.key === key ? { ...q, ...next, aiDrafted: false } : q)),
    );
  }

  const incomplete = questions.filter(
    (q) => q.prompt.trim() === '' || q.options.some((o) => o.text.trim() === ''),
  ).length;
  const canSave = questions.length > 0 && incomplete === 0 && !saveMutation.isPending;
  const unreviewed = questions.filter((q) => q.aiDrafted).length;

  return (
    <Card className="space-y-4 p-5 print:hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-kw-shark">
            <GraduationCap className="size-4 text-kw-cerulean-dark" /> Comprehension test
          </h2>
          <p className="mt-1 max-w-prose text-[13px] text-kw-muted">
            While this test is active, passing it is the only way to satisfy the re-acknowledgment obligation
            for this SOP — the plain “I have read and understood” button is refused.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          isLoading={suggestMutation.isPending}
          onClick={() => {
            suggestMutation.mutate();
          }}
        >
          <Sparkles className="size-4" /> Draft with AI
        </Button>
      </div>

      {unreviewed > 0 ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
          <p className="text-[12px] text-kw-warning-text">
            {unreviewed} AI-drafted question(s) haven’t been reviewed. Editing one marks it reviewed.
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Pass threshold (%)</span>
          <Input
            type="number"
            min={1}
            max={100}
            value={String(threshold)}
            onChange={(e) => {
              setThreshold(Number(e.target.value));
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Max attempts</span>
          <Input
            type="number"
            min={1}
            max={20}
            placeholder="Unlimited"
            value={maxAttempts}
            onChange={(e) => {
              setMaxAttempts(e.target.value);
            }}
          />
          <span className="text-[11px] text-kw-ghost">Per revision — a new revision resets it.</span>
        </label>
        <label className="flex items-start gap-2 pt-6">
          <input
            type="checkbox"
            checked={active}
            disabled={questions.length === 0}
            onChange={(e) => {
              setActive(e.target.checked);
            }}
            className="mt-0.5"
          />
          <span className="text-[13px] text-kw-shark">
            Active
            <span className="block text-[11px] text-kw-ghost">
              {questions.length === 0 ? 'Add a question first.' : 'Required to pass before acknowledging.'}
            </span>
          </span>
        </label>
      </div>

      <div className="space-y-3">
        {questions.map((q, qi) => (
          <div key={q.key} className="rounded-kw-md border border-kw-border p-3">
            <div className="flex items-start gap-2">
              <span className="mt-2 text-[12px] font-medium text-kw-muted">{qi + 1}.</span>
              <div className="min-w-0 flex-1 space-y-2">
                <Input
                  placeholder="Question"
                  value={q.prompt}
                  onChange={(e) => {
                    patch(q.key, { prompt: e.target.value });
                  }}
                />
                {q.options.map((opt) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${q.key}`}
                      checked={q.correctOptionId === opt.id}
                      onChange={() => {
                        patch(q.key, { correctOptionId: opt.id });
                      }}
                      aria-label={`Mark option ${opt.id} correct`}
                    />
                    <Input
                      className="flex-1"
                      placeholder="Answer option"
                      value={opt.text}
                      onChange={(e) => {
                        patch(q.key, {
                          options: q.options.map((o) =>
                            o.id === opt.id ? { ...o, text: e.target.value } : o,
                          ),
                        });
                      }}
                    />
                    {q.options.length > 2 ? (
                      <button
                        type="button"
                        aria-label="Remove option"
                        onClick={() => {
                          patch(q.key, {
                            options: q.options.filter((o) => o.id !== opt.id),
                            ...(q.correctOptionId === opt.id
                              ? { correctOptionId: q.options.find((o) => o.id !== opt.id)?.id ?? 'o1' }
                              : {}),
                          });
                        }}
                        className="text-kw-muted hover:text-kw-danger-text"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    ) : null}
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  {q.options.length < 6 ? (
                    <button
                      type="button"
                      onClick={() => {
                        patch(q.key, {
                          options: [...q.options, { id: `o${String(q.options.length + 1)}`, text: '' }],
                        });
                      }}
                      className="text-[12px] text-kw-cerulean-dark hover:underline"
                    >
                      Add option
                    </button>
                  ) : null}
                  {q.aiDrafted ? (
                    <span className="text-[11px] uppercase tracking-wide text-kw-warning-text">
                      AI draft — unreviewed
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setQuestions((prev) => prev.filter((x) => x.key !== q.key));
                    }}
                    className="ml-auto text-[12px] text-kw-muted hover:text-kw-danger-text"
                  >
                    Remove question
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setQuestions((prev) => [...prev, blankQuestion()]);
          }}
        >
          <Plus className="size-4" /> Add question
        </Button>
        <Button
          type="button"
          className="ml-auto"
          isLoading={saveMutation.isPending}
          disabled={!canSave}
          onClick={() => {
            saveMutation.mutate();
          }}
        >
          <Check className="size-4" /> Save test
        </Button>
      </div>
      {incomplete > 0 ? (
        <p className="text-[12px] text-kw-danger-text">
          {incomplete} question(s) still have an empty prompt or option.
        </p>
      ) : null}
    </Card>
  );
}
